import React, { useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { UserRole } from "@de-common/types/UserRole";

import { useRoles } from "../hooks/useRoles";
import tokenManager from "../utils/tokenManager";

export interface RequireAuthProps {
  requiredRoles?: UserRole[];
  roleRequirement?: "any" | "all";
  children: React.ReactElement;
}

/*
  RequireAuth is a wrapper component for protecting routes.
  It checks if the user is authenticated and, if a requiredRoles array is specified,
  whether the user has the necessary roles based on the roleRequirement criteria.
  If the conditions are not met, it redirects accordingly. Otherwise, it renders its children.
*/
export const RequireAuth: React.FC<RequireAuthProps> = ({
  requiredRoles,
  roleRequirement = "all",
  children,
}) => {
  const location = useLocation();
  const userData = tokenManager.getUserData();
  const roles = useRoles();
  // System administrators are always allowed.
  const isSysAdmin = roles?.includes(UserRole.SYSTEM_ADMIN);

  if (!userData) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  const hasAllRequiredRoles = useCallback(
    () => requiredRoles?.every((role) => roles?.includes(role)),
    [requiredRoles, roles],
  );
  const hasAnyRequiredRole = useCallback(
    () => requiredRoles?.some((role) => roles?.includes(role)),
    [requiredRoles, roles],
  );

  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRoles =
      roleRequirement === "all" ? hasAllRequiredRoles() : hasAnyRequiredRole();

    if (!hasRequiredRoles && !isSysAdmin) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return children;
};
